import { useEffect } from 'react';

const APP_NAME = 'Portal do Colaborador';

const resolvePublicAssetUrl = (url) => {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    return `${process.env.REACT_APP_API_URL}${url.startsWith('/') ? url : `/${url}`}`;
};

/**
 * Prepara o portal para ser "adicionado à tela inicial" como app.
 *
 * - Nome fixo "Portal do Colaborador" (manifest + meta tags iOS).
 * - Ícone usa a logo da empresa quando houver; senão, os ícones padrão do app.
 * - start_url aponta para a URL atual do portal, então no Android o ícone abre
 *   direto na agenda (e não na raiz do admin).
 *
 * Espelha a técnica já usada na PublicBookingPage (manifest dinâmico via blob).
 */
export default function usePortalInstall(companyInfo) {
    useEffect(() => {
        if (!('serviceWorker' in navigator)) return undefined;

        // O escopo por empresa mantém a navegação do PWA dentro do portal daquele tenant.
        const slug = window.location.pathname.split('/')[2];
        if (!slug) return undefined;

        navigator.serviceWorker.register('/portal-service-worker.js', {
            scope: `/p/${encodeURIComponent(slug)}/`,
        }).catch((error) => {
            // A instalação continua disponível; apenas recursos offline/push dependem do worker.
            console.warn('Não foi possível preparar o portal para instalação.', error);
        });

        return undefined;
    }, []);

    useEffect(() => {
        const logoUrl = companyInfo?.logoUrl ? resolvePublicAssetUrl(companyInfo.logoUrl) : null;
        const themeColor = companyInfo?.headerColor || '#0f172a';
        const origin = window.location.origin;

        const manifest = {
            name: APP_NAME,
            short_name: 'Portal',
            icons: logoUrl
                ? [
                    { src: logoUrl, sizes: '192x192' },
                    { src: logoUrl, sizes: '512x512' },
                  ]
                : [
                    { src: `${origin}/web-app-manifest-192x192.png`, sizes: '192x192', type: 'image/png', purpose: 'any maskable' },
                    { src: `${origin}/web-app-manifest-512x512.png`, sizes: '512x512', type: 'image/png', purpose: 'any maskable' },
                  ],
            start_url: window.location.href,
            scope: `/p/${encodeURIComponent(window.location.pathname.split('/')[2] || '')}/`,
            display: 'standalone',
            theme_color: themeColor,
            background_color: '#ffffff',
        };

        const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' });
        const blobUrl = URL.createObjectURL(blob);

        // --- Manifest (Android Chrome) ---
        let manifestLink = document.querySelector('link[rel="manifest"]');
        const prevManifestHref = manifestLink ? manifestLink.getAttribute('href') : null;
        if (!manifestLink) {
            manifestLink = document.createElement('link');
            manifestLink.rel = 'manifest';
            document.head.appendChild(manifestLink);
        }
        manifestLink.setAttribute('href', blobUrl);

        // --- Apple Touch Icon (iOS ignora o manifest via JS) ---
        let appleIcon = document.querySelector('link[rel="apple-touch-icon"]');
        const prevAppleHref = appleIcon ? appleIcon.getAttribute('href') : null;
        if (logoUrl) {
            if (!appleIcon) {
                appleIcon = document.createElement('link');
                appleIcon.rel = 'apple-touch-icon';
                document.head.appendChild(appleIcon);
            }
            appleIcon.setAttribute('href', logoUrl);
        }

        // --- Meta tags iOS: tela cheia + nome do app ---
        const metas = [
            { name: 'apple-mobile-web-app-capable', content: 'yes' },
            { name: 'apple-mobile-web-app-status-bar-style', content: 'default' },
            { name: 'apple-mobile-web-app-title', content: APP_NAME },
            { name: 'theme-color', content: themeColor },
        ];
        const createdMetas = [];
        const prevMetaContent = {};
        metas.forEach(({ name, content }) => {
            let tag = document.querySelector(`meta[name="${name}"]`);
            if (tag) {
                prevMetaContent[name] = tag.getAttribute('content');
            } else {
                tag = document.createElement('meta');
                tag.setAttribute('name', name);
                document.head.appendChild(tag);
                createdMetas.push(tag);
            }
            tag.setAttribute('content', content);
        });

        const prevTitle = document.title;
        document.title = APP_NAME;

        return () => {
            URL.revokeObjectURL(blobUrl);
            if (manifestLink) {
                if (prevManifestHref) manifestLink.setAttribute('href', prevManifestHref);
                else manifestLink.remove();
            }
            if (appleIcon && prevAppleHref) appleIcon.setAttribute('href', prevAppleHref);
            Object.entries(prevMetaContent).forEach(([name, content]) => {
                const tag = document.querySelector(`meta[name="${name}"]`);
                if (tag && content != null) tag.setAttribute('content', content);
            });
            createdMetas.forEach((tag) => tag.remove());
            document.title = prevTitle;
        };
    }, [companyInfo]);
}
